// ============================================
// UBUS V2.1 数据库类型定义
// 基于 PostgreSQL + PostGIS
// ============================================

/**
 * Supabase Database 类型（用于 SupabaseClient 泛型参数）
 * 
 * 使用 [key: string]: any 索引签名作为 fallback，
 * 因为无法通过 supabase CLI 自动生成严格类型。
 * 如需严格类型，可运行: npx supabase gen types typescript --project-id <id> > database.types.ts
 */
export interface Database {
  public: {
    Tables: {
      vehicles: { Row: Vehicle; Insert: Partial<Vehicle>; Update: Partial<Vehicle> };
      drivers: { Row: Driver; Insert: Partial<Driver>; Update: Partial<Driver> };
      trips: { Row: Trip; Insert: Partial<Trip>; Update: Partial<Trip> };
      location_logs: { Row: LocationLog; Insert: Partial<LocationLog>; Update: Partial<LocationLog> };
      sos_events: { Row: SOSEvent; Insert: Partial<SOSEvent>; Update: Partial<SOSEvent> };
      organizations: { Row: Organization; Insert: Partial<Organization>; Update: Partial<Organization> };
      settings: { Row: Setting; Insert: Partial<Setting>; Update: Partial<Setting> };
      trip_feedback: { Row: TripFeedback; Insert: Partial<TripFeedback>; Update: Partial<TripFeedback> };
      vehicle_inspections: { Row: VehicleInspection; Insert: Partial<VehicleInspection>; Update: Partial<VehicleInspection> };
      fuel_expenses: { Row: FuelExpense; Insert: Partial<FuelExpense>; Update: Partial<FuelExpense> };
      delayed_trip_alerts: { Row: DelayedTripAlert; Insert: Partial<DelayedTripAlert>; Update: Partial<DelayedTripAlert> };
      trip_documents: { Row: TripDocument; Insert: Partial<TripDocument>; Update: Partial<TripDocument> };
      fleet_operators: { Row: FleetOperator; Insert: Partial<FleetOperator>; Update: Partial<FleetOperator> };
      reminder_types: { Row: ReminderType; Insert: Partial<ReminderType>; Update: Partial<ReminderType> };
      reminder_configs: { Row: ReminderConfig; Insert: Partial<ReminderConfig>; Update: Partial<ReminderConfig> };
      reminder_events: { Row: ReminderEvent; Insert: Partial<ReminderEvent>; Update: Partial<ReminderEvent> };
      reminder_deliveries: { Row: ReminderDelivery; Insert: Partial<ReminderDelivery>; Update: Partial<ReminderDelivery> };
      reminder_reads: { Row: ReminderRead; Insert: Partial<ReminderRead>; Update: Partial<ReminderRead> };
      reminder_source_fields: {
        Row: ReminderSourceFieldRow;
        Insert: Partial<ReminderSourceFieldRow>;
        Update: Partial<ReminderSourceFieldRow>;
      };
      message_templates: {
        Row: MessageTemplate;
        Insert: Partial<MessageTemplate>;
        Update: Partial<MessageTemplate>;
      };
    };
    Functions: {
      recommend_rescue_vehicles: {
        Args: { p_sos_id: string };
        Returns: RescueVehicleRecommendation[];
      };
      recommend_dispatch_assignments: {
        Args: { p_trip_id: string; p_limit?: number };
        Returns: DispatchRecommendation[];
      };
      check_vehicle_schedule_conflict: {
        Args: {
          p_vehicle_id: string;
          p_date: string;
          p_pickup_time: string;
          p_end_time: string;
          p_exclude_trip_id?: string | null;
          p_buffer_minutes?: number | null;
        };
        Returns: ScheduleConflictRow[];
      };
      check_driver_schedule_conflict: {
        Args: {
          p_driver_id: string;
          p_date: string;
          p_pickup_time: string;
          p_end_time: string;
          p_exclude_trip_id?: string | null;
          p_buffer_minutes?: number | null;
        };
        Returns: ScheduleConflictRow[];
      };
      recommend_fleet_operators_for_trip: {
        Args: { p_trip_id: string; p_limit?: number };
        Returns: FleetOperatorRecommendation[];
      };
      assign_platform_trip_to_owner: {
        Args: {
          p_trip_id: string;
          p_fleet_operator_id: string;
          p_vehicle_id: string;
          p_driver_id: string;
        };
        Returns: Trip;
      };
      accept_platform_trip: {
        Args: { p_trip_id: string };
        Returns: Trip;
      };
      reject_platform_trip: {
        Args: { p_trip_id: string; p_reason?: string | null };
        Returns: Trip;
      };
      vehicle_availability_status: {
        Args: {
          p_date?: string;
          p_window_start?: string | null;
          p_window_end?: string | null;
        };
        Returns: VehicleAvailabilityRow[];
      };
    };
    Views: Record<string, never>;
    Enums: Record<string, never>;
    CompositeTypes: Record<string, never>;
  };
  [key: string]: any;
}

export type VehicleFeature = 'wheelchair' | 'ac' | 'coach' | 'seatbelts';

export const VEHICLE_FEATURE_OPTIONS: VehicleFeature[] = [
  'wheelchair',
  'ac',
  'coach',
  'seatbelts',
];

export interface Vehicle {
  id: string;                      // UUID
  fleet_number: string;            // 唯一编号，如 "UB-001"
  model: string;                   // 车型，如 "Mercedes-Benz Sprinter"
  capacity: number;               // 座位数，如 57
  year: number;                  // 年份，如 2023
  status: 'active' | 'maintenance' | 'retired';
  photo_url?: string;             // Supabase Storage 公共 URL
  notes?: string;
  status_updated_at?: string;
  registration_plate?: string | null;
  registration_expiry?: string;
  /** Vehicle insurance expiry (optional). */
  insurance_expiry?: string | null;
  /** Last service / maintenance date. */
  last_service_at?: string | null;
  /** Last HVIS inspection date. */
  hvis_last_at?: string | null;
  /** Next HVIS due (optional override). */
  hvis_next_due?: string | null;
  features?: string[];
  preferred_driver_id?: string | null;
  last_known_location?: string;
  created_at: string;             // ISO 8601 时间戳
  updated_at: string;
  created_by: string;             // Supabase Auth UID
  fleet_operator_id: string;
  accepting_tasks?: boolean;
  accepting_tasks_at?: string | null;
}

export interface Driver {
  id: string;
  auth_uid: string;               // Supabase Auth UID
  name: string;
  phone: string;                 // 手机号，如 "+61412345678"
  /** Optional contact email (invites / setup link); login remains phone @ubus.local */
  email?: string | null;
  license_number: string;        // 驾照号，唯一
  license_expiry: string;        // ISO 8601 日期 (YYYY-MM-DD)
  /** Working With Children Check expiry (optional). */
  wwcc_expiry?: string | null;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  status: 'active' | 'inactive';
  license_photo_url?: string;    // Storage path or legacy public URL
  notes?: string;
  preferred_vehicle_id?: string | null;
  accepting_tasks?: boolean;
  accepting_tasks_at?: string | null;
  accepting_tasks_location?: string | null;
  created_at: string;
  updated_at: string;
  fleet_operator_id: string;
}

export type TripSource = 'owner' | 'platform' | 'hq';
export type TripAssignmentStatus = 'n/a' | 'pending_owner' | 'accepted' | 'rejected';

export type JobType = 'school_run' | 'charter_standby' | 'tour' | 'transfer';
export type JobStatus = 'unscheduled' | 'planning' | 'scheduled' | 'cancelled';
export type JobLegType = 'transfer' | 'standby_start' | 'standby_block';
export type JobSource = 'hq' | 'client_request' | 'fleet';
export type JobAcceptanceStatus = 'n/a' | 'pending' | 'accepted' | 'rejected';
export type JobAssistStatus = 'none' | 'pending_platform' | 'reassigned';

export interface Job {
  id: string;
  /** Business order number e.g. U20260727001 / C20260727001 */
  job_number?: string;
  org_id?: string | null;
  schedule_date: string;
  job_type: JobType;
  title: string;
  passenger_count: number;
  requirements?: string | null;
  status: JobStatus;
  source?: JobSource;
  acceptance_status?: JobAcceptanceStatus;
  rejection_reason?: string | null;
  assigned_vehicle_id?: string | null;
  notes?: string | null;
  /** Optional preferred fleet — daily schedule only uses this fleet's vehicles when set. */
  fleet_operator_id?: string | null;
  assist_status?: JobAssistStatus;
  assist_requested_at?: string | null;
  assist_requested_by?: string | null;
  assist_reason?: string | null;
  assist_from_fleet_operator_id?: string | null;
  assist_resolved_at?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  split_group_id?: string | null;
  split_from_job_id?: string | null;
  /** Driver exclusive for schedule_date — no other jobs that day. */
  driver_full_day_escort?: boolean;
  created_at: string;
  updated_at: string;
  job_legs?: JobLeg[];
  organization?: Organization;
}

export type JobLifecycleEventType =
  | 'assist_requested'
  | 'assist_cancelled'
  | 'fleet_reassigned';

export interface JobLifecycleEvent {
  id: string;
  job_id: string;
  event_type: JobLifecycleEventType;
  from_fleet_operator_id?: string | null;
  to_fleet_operator_id?: string | null;
  actor_uid?: string | null;
  note?: string | null;
  created_at: string;
}

export interface JobLeg {
  id: string;
  job_id: string;
  sequence: number;
  leg_type: JobLegType;
  pickup_address: string;
  pickup_geopoint?: string | null;
  dropoff_address: string;
  dropoff_geopoint?: string | null;
  earliest_start: string;
  latest_start?: string | null;
  end_time?: string | null;
  service_duration_minutes: number;
  notes?: string | null;
  trip_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Trip {
  id: string;
  /** Business trip number e.g. T20260727001 */
  trip_number?: string;
  org_id?: string | null;       // 关联客户组织（可为空）
  /** Trip-level contact (optional; may differ from organization). */
  contact_person?: string | null;
  contact_phone?: string | null;
  date: string;                 // ISO 8601 日期 (YYYY-MM-DD)
  pickup_time: string;          // ISO 8601 时间 (HH:MM:SS)
  end_time: string;             // Expected end time (HH:MM:SS), same date
  pickup_address: string;
  pickup_geopoint?: string;     // PostGIS WKT 格式："SRID=4326;POINT(lng lat)"
  dropoff_address: string;
  dropoff_geopoint?: string;
  passenger_count: number;
  special_notes?: string;
  /** Storage paths in uploads bucket (assign vehicle/driver attachments). */
  attachment_urls?: string[] | null;
  status: 'scheduled' | 'dispatched' | 'in_progress' | 'completed' | 'cancelled';
  vehicle_id?: string | null;    // 分配的车辆
  driver_id?: string | null;   // 分配的司机
  cancelled_reason?: string;
  /** When status first becomes cancelled. */
  cancelled_at?: string | null;
  fleet_operator_id?: string;
  source?: TripSource;
  assignment_status?: TripAssignmentStatus;
  vehicle_preference?: string;
  tracking_active?: boolean;
  /** Actual start when status first becomes in_progress. */
  started_at?: string | null;
  completed_at?: string;
  job_id?: string | null;
  job_leg_id?: string | null;
  trip_group_id?: string | null;
  /** Driver exclusive for this trip date — no other jobs that day. */
  driver_full_day_escort?: boolean;
  /** Pickup→dropoff drive minutes (Google or static). Separate from end_time. */
  route_drive_minutes?: number | null;
  /** Pickup→dropoff drive distance meters (Google or static). */
  route_drive_distance_meters?: number | null;
  /** pickup_time + route_drive_minutes (display ETA). Separate from end_time occupancy. */
  estimated_dropoff_time?: string | null;
  route_drive_source?: 'directions' | 'static' | 'static_fallback' | 'unavailable' | null;
  route_drive_updated_at?: string | null;
  /** Set when pre-pickup customer notify was sent (cron dedupe). */
  customer_pre_trip_notified_at?: string | null;
  /** Public SMS share link token (unguessable). */
  share_token?: string | null;
  /** End of trip date Australia/Sydney. */
  share_token_expires_at?: string | null;
  created_at: string;
  updated_at: string;
  // 以下为联表查询返回的关联数据
  vehicle?: Vehicle;
  driver?: Driver;
  organization?: Organization;
  // Plural aliases — Supabase 联表查询结果使用复数表名
  vehicles?: Vehicle;
  drivers?: Driver;
  organizations?: Organization;
}

export interface LocationLog {
  id: string;
  /** Null for shift GPS before a trip has started (CONF-20260716). */
  trip_id?: string | null;
  driver_id: string;
  vehicle_id?: string | null;
  geopoint: string;             // PostGIS WKT 格式："SRID=4326;POINT(lng lat)"
  timestamp: string;            // ISO 8601 时间戳
  accuracy?: number;             // GPS 精度（米）
  speed?: number;                // 速度（km/h）
}

export interface SOSEvent {
  id: string;
  trip_id: string;
  reporter_id: string;           // 触发人 UID
  reporter_role: 'driver' | 'client';
  event_type: string;            // 车辆故障/交通事故/乘客紧急/车辆未出现/其他
  description?: string;
  contact_phone?: string;
  photo_url?: string;
  location?: string;             // PostGIS WKT 格式
  priority?: 'P0' | 'P1' | 'P2';
  status: 'new' | 'handling' | 'resolved';
  handled_by?: string;           // 处理人 UID
  handling_notes?: string;
  handling_method?: string;
  follow_up?: string;
  sms_sent_at?: string;
  sms_recipients?: string[];
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: string;
  name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;         // 用于接收通知
  address?: string;
  notification_emails: string[]; // 额外通知邮箱列表
  requirements?: string;
  geopoint?: string;
  /** Optional preferred fleet (档案展示 only; not used by scheduling). */
  preferred_fleet_operator_id?: string | null;
  /** Optional customer category (school, travel agency, etc.). */
  customer_type?: OrganizationCustomerType | null;
  created_at: string;
  updated_at: string;
}

export type OrganizationCustomerType =
  | 'school'
  | 'travel_agency'
  | 'government'
  | 'social_org'
  | 'other';

export const ORGANIZATION_CUSTOMER_TYPES: OrganizationCustomerType[] = [
  'school',
  'travel_agency',
  'government',
  'social_org',
  'other',
];

export interface FleetOperator {
  id: string;
  name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  address?: string;
  geopoint?: string;
  status: 'active' | 'inactive';
  auth_uid?: string;
  /** Auth user id of the fleet's primary manager (Task 5). Falls back to auth_uid when null. */
  primary_manager_uid?: string | null;
  /** Fleet comprehensive insurance expiry. */
  comprehensive_insurance_expiry?: string | null;
  /** Fleet public liability insurance expiry. */
  public_liability_expiry?: string | null;
  created_at: string;
  updated_at: string;
}

export type ReminderChannel = 'in_app_admin' | 'in_app_driver' | 'email' | 'sms';
export type ReminderRuleKind = 'ahead_days' | 'interval_ahead';
export type ReminderCategory = 'driver' | 'vehicle' | 'fleet';

export type MessageTemplateChannel = 'sms' | 'email';

export interface MessageTemplate {
  id: string;
  template_key: string;
  channel: MessageTemplateChannel;
  locale: string;
  name: string;
  subject?: string | null;
  body: string;
  variables: string[] | unknown;
  enabled: boolean;
  updated_at: string;
  updated_by?: string | null;
}

export interface ReminderType {
  code: string;
  name_en: string;
  name_zh: string;
  category: ReminderCategory;
  source_field: string;
  rule_kind: ReminderRuleKind;
  default_interval_days?: number | null;
  sort_order: number;
  /** Deprecated flag; all reminder types are user-managed. */
  is_system?: boolean;
  created_at: string;
}

export interface ReminderConfig {
  type_code: string;
  enabled: boolean;
  ahead_days: number[];
  interval_days?: number | null;
  channels: ReminderChannel[];
  emails: string[];
  phones: string[];
  updated_at: string;
  updated_by?: string | null;
}

export interface ReminderEvent {
  id: string;
  event_key: string;
  type_code: string;
  subject_kind: 'driver' | 'vehicle' | 'fleet_operator';
  subject_id: string;
  subject_label?: string | null;
  due_date: string;
  ahead_days?: number | null;
  status: 'open' | 'resolved' | 'cancelled';
  payload: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ReminderDelivery {
  id: string;
  event_id: string;
  channel: ReminderChannel;
  recipient?: string | null;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  error_message?: string | null;
  sent_at?: string | null;
  created_at: string;
}

export interface ReminderRead {
  id: string;
  event_id: string;
  reader_kind: 'admin_user' | 'driver';
  reader_id: string;
  reader_label?: string | null;
  read_at: string;
}

export interface ReminderSourceFieldRow {
  source_field: string;
  category: ReminderCategory;
  subject_kind: 'driver' | 'vehicle' | 'fleet_operator';
  table_name: 'drivers' | 'vehicles' | 'fleet_operators';
  due_column: string;
  select_extra: string[];
  rule_kinds: ReminderRuleKind[];
  default_interval_days?: number | null;
  next_due_column?: string | null;
  driver_in_app: boolean;
  label_en: string;
  label_zh: string;
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Setting {
  id: string;
  key: string;
  value: unknown;
  created_at: string;
  updated_at: string;
}

export interface TripFeedback {
  id: string;
  trip_id: string;
  org_id: string;
  reporter_id: string;
  rating: number;
  category: string;
  message: string;
  status: 'new' | 'reviewed';
  created_at: string;
}

export type InspectionSeverityResult = 'pass' | 'pass_with_notes' | 'blocked';

export interface InspectionChecklistItemRow {
  id: string;
  status: 'pass' | 'fail' | 'na';
  note?: string;
}

export interface InspectionBodyDefect {
  view: 'front' | 'back' | 'left' | 'right';
  note?: string;
  photo_url?: string;
}

export interface InspectionWorkFields {
  date?: string;
  rego?: string;
  driver_name?: string;
  tour_name?: string;
  pick_up?: string;
  drop_off?: string;
  start_km?: string;
  finish_km?: string;
  hotel_name?: string;
  depot_start_time?: string;
  depot_finish_time?: string;
  fuel_level?: string;
  /** Fitness to Drive Pre-Start Declaration answers (CONF-20260724). */
  fitness?: {
    answers: Record<string, boolean | null | undefined>;
    signed_name?: string;
    signed_at?: string;
    form_ver: string;
  };
}

export interface VehicleInspection {
  id: string;
  driver_id: string;
  vehicle_id?: string | null;
  trip_id?: string | null;
  inspection_type: 'pre-trip' | 'post-trip';
  inspection_date: string;
  checked_items: string[];
  items: InspectionChecklistItemRow[];
  body_defects: InspectionBodyDefect[];
  work_fields: InspectionWorkFields;
  declaration_accepted_at?: string | null;
  declaration_text_ver?: string | null;
  severity_result?: InspectionSeverityResult | null;
  photo_urls: string[];
  after_items: InspectionChecklistItemRow[];
  after_completed_at?: string | null;
  confirmed_trip_ids: string[];
  notes?: string;
  created_at: string;
}

export interface FuelExpense {
  id: string;
  driver_id: string;
  trip_id?: string;
  expense_date: string;
  type: 'fuel' | 'toll' | 'repair' | 'insurance' | 'other';
  amount: number;
  description?: string;
  receipt_url?: string;
  created_at: string;
}

export interface DelayedTripAlert {
  id: string;
  trip_id: string;
  minutes_delayed?: number;
  notified_at: string;
}

// ============================================
// Supabase Realtime 订阅类型
// ============================================

export interface RealtimePayload<T> {
  schema: string;
  table: string;
  commit_timestamp: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: T;
  old: Partial<T>;
}

// ============================================
// API 响应类型
// ============================================

export interface ApiResponse<T> {
  data: T | null;
  error: Error | null;
  count?: number;
  status?: number;
  statusText?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============================================
// 表单类型
// ============================================

export interface VehicleFormData {
  fleet_number: string;
  model: string;
  capacity: number;
  year: number;
  status: 'active' | 'maintenance' | 'retired';
  photo?: File;
  registration_plate: string;
  registration_expiry?: string;
  features?: VehicleFeature[];
  preferred_driver_id?: string | null;
  notes?: string;
  fleet_operator_id?: string;
}

export interface DriverFormData {
  name: string;
  phone: string;
  email?: string | null;
  license_number: string;
  license_expiry: string;
  wwcc_expiry?: string | null;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  status: 'active' | 'inactive';
  license_photo?: File;
  preferred_vehicle_id?: string | null;
  notes?: string;
  fleet_operator_id?: string;
}

export interface TripFormData {
  org_id: string;
  date: string;
  pickup_time: string;
  pickup_address: string;
  dropoff_address: string;
  passenger_count: number;
  special_notes?: string;
}

export interface SOSFormData {
  event_type: string;
  description?: string;
  photo?: File;
}

export interface OrganizationFormData {
  name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  address?: string;
  notification_emails?: string[];
  requirements?: string;
  preferred_fleet_operator_id?: string | null;
}

// ============================================
// P2 extension types
// ============================================

export interface TripDocument {
  id: string;
  trip_id: string;
  org_id: string;
  doc_type: 'itinerary' | 'invoice';
  file_path: string;
  created_at: string;
  created_by?: string;
}

export interface RescueVehicleRecommendation {
  vehicle_id: string;
  fleet_number: string;
  model: string;
  capacity: number;
  distance_meters: number | null;
}

export interface ScheduleConflictRow {
  conflicting_trip_id: string;
  pickup_time: string;
  end_time: string;
  pickup_address: string;
  status: string;
}

export interface DispatchRecommendation {
  vehicle_id: string;
  fleet_number: string;
  model: string;
  capacity: number;
  driver_id: string;
  driver_name: string;
  score: number;
  distance_meters: number | null;
  breakdown: {
    distance_score: number;
    capacity_fit_score: number;
    utilization_score: number;
    requirements_score: number;
    anchor_type?: string;
  };
}

export interface FleetOperatorRecommendation {
  fleet_operator_id: string;
  operator_name: string;
  vehicle_id: string;
  fleet_number: string;
  model: string;
  capacity: number;
  driver_id: string;
  driver_name: string;
  score: number;
  distance_meters: number | null;
  breakdown: {
    distance_score: number;
    capacity_fit_score: number;
  };
}

export type VehicleAvailabilityStatus =
  | 'available'
  | 'busy'
  | 'maintenance'
  | 'pending_decision';

export interface VehicleAvailabilityRow {
  vehicle_id: string;
  fleet_number: string;
  model: string;
  capacity: number;
  fleet_operator_id: string;
  operator_name: string;
  vehicle_status: string;
  availability_status: VehicleAvailabilityStatus;
  busy_from: string | null;
  busy_until: string | null;
  trip_count: number;
}

// ============================================
// 用户认证类型
// ============================================

export interface AuthUser {
  id: string;
  email?: string;
  phone?: string;
  role?: 'admin' | 'driver' | 'client';
  org_id?: string;
  driver_id?: string;
}

export interface LoginCredentials {
  email?: string;
  phone?: string;
  password: string;
}

// ============================================
// Google Maps 类型
// ============================================

export interface GeocodeResult {
  lat: number;
  lng: number;
  formatted_address: string;
}

export interface ETAResult {
  duration: number;      // 秒
  duration_in_traffic?: number;
  distance: number;      // 米
  eta_text: string;      // 如 "5 mins"
}

// ============================================
// 环境配置类型
// ============================================

export interface EnvConfig {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_GOOGLE_MAPS_API_KEY: string;
  VITE_MAPS_LIBRARY_KEY?: string;
}
